import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";

interface AddEventSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onEventAdded?: () => void;
  defaultDate?: string;
  defaultAssignee?: string;
}

export function AddEventSheet({
  isOpen,
  onClose,
  onEventAdded,
  defaultDate,
  defaultAssignee,
}: AddEventSheetProps) {
  // State for form fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState(defaultDate || "");
  const [endDate, setEndDate] = useState(defaultDate || "");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [isAllDay, setIsAllDay] = useState(false);
  const [eventType, setEventType] = useState<string>("meeting");
  const [location, setLocation] = useState("");
  const [address, setAddress] = useState("");
  const [assignedStaff, setAssignedStaff] = useState<string[]>(
    defaultAssignee ? [defaultAssignee] : []
  );
  const [businessId, setBusinessId] = useState<string>("none");
  const [serviceLocationId, setServiceLocationId] = useState<string>("none");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch organization members
  const members = useQuery(api.calendar.getOrgMembers) || [];

  // Fetch businesses for context linking
  const businesses = useQuery(api.businesses.getBusinesses) || [];

  // Fetch service locations
  const serviceLocations = useQuery(api.calendar.getServiceLocations) || [];

  // Create event mutation
  const createEvent = useMutation(api.calendar.createEvent);

  // Handle form submission
  const handleSubmit = async () => {
    if (!title || !startDate || !startTime || !endTime || assignedStaff.length === 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      await createEvent({
        title,
        description,
        startDate,
        endDate: endDate || startDate,
        startTime,
        endTime,
        isAllDay,
        eventType: eventType as any,
        businessId:
          businessId && businessId !== "none" ? (businessId as Id<"businesses">) : undefined,
        serviceLocationId:
          serviceLocationId && serviceLocationId !== "none"
            ? (serviceLocationId as Id<"serviceLocations">)
            : undefined,
        location,
        address,
        assignedStaff: assignedStaff as Id<"users">[],
      });

      // Reset form
      setTitle("");
      setDescription("");
      setStartDate(defaultDate || "");
      setEndDate(defaultDate || "");
      setStartTime("09:00");
      setEndTime("10:00");
      setIsAllDay(false);
      setEventType("meeting");
      setLocation("");
      setAddress("");
      setAssignedStaff(defaultAssignee ? [defaultAssignee] : []);
      setBusinessId("none");
      setServiceLocationId("none");

      if (onEventAdded) onEventAdded();
      onClose();
    } catch (error) {
      console.error("Error creating event:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStaffToggle = (memberId: string) => {
    setAssignedStaff((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]
    );
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[500px] overflow-y-auto sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Add Calendar Event</SheetTitle>
          <SheetDescription>Create a new event and assign it to team members.</SheetDescription>
        </SheetHeader>
        <div className="space-y-6 py-6">
          <div className="space-y-2">
            <Label htmlFor="title">Event Title</Label>
            <Input
              id="title"
              placeholder="Enter event title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Enter event description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="event-type">Event Type</Label>
            <Select value={eventType} onValueChange={setEventType}>
              <SelectTrigger>
                <SelectValue placeholder="Select event type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="service">Service</SelectItem>
                <SelectItem value="meeting">Meeting</SelectItem>
                <SelectItem value="training">Training</SelectItem>
                <SelectItem value="walkthrough">Walkthrough</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="all-day"
              checked={isAllDay}
              onCheckedChange={(checked) => setIsAllDay(checked as boolean)}
            />
            <Label htmlFor="all-day">All day event</Label>
          </div>

          {!isAllDay && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-time">Start Time</Label>
                <Input
                  id="start-time"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-time">End Time</Label>
                <Input
                  id="end-time"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              placeholder="Enter location (optional)"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              placeholder="Enter address (optional)"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="business">Related Business (Optional)</Label>
            <Select value={businessId} onValueChange={setBusinessId}>
              <SelectTrigger>
                <SelectValue placeholder="Select business" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No business</SelectItem>
                {businesses.map((business: any) => (
                  <SelectItem key={business._id} value={business._id}>
                    {business.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="service-location">Service Location (Optional)</Label>
            <Select value={serviceLocationId} onValueChange={setServiceLocationId}>
              <SelectTrigger>
                <SelectValue placeholder="Select service location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No service location</SelectItem>
                {serviceLocations.map((location: any) => (
                  <SelectItem key={location.id} value={location.id}>
                    {location.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Assign to Team Members</Label>
            <div className="max-h-32 space-y-2 overflow-y-auto rounded-md border p-2">
              {members.length === 0 ? (
                <p className="text-sm text-gray-500">No team members found</p>
              ) : (
                members.map((member: any) => (
                  <div key={member._id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`member-${member._id}`}
                      checked={assignedStaff.includes(member._id)}
                      onCheckedChange={() => handleStaffToggle(member._id)}
                    />
                    <Label htmlFor={`member-${member._id}`} className="text-sm">
                      {member.name}
                    </Label>
                  </div>
                ))
              )}
            </div>
          </div>

          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={isSubmitting || !title || !startDate || assignedStaff.length === 0}
          >
            {isSubmitting ? "Creating..." : "Create Event"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
